import {
  ApolloClient,
  ApolloLink,
  ApolloQueryResult,
  DefaultContext,
  FetchResult,
  InMemoryCache,
  MutationOptions,
  NormalizedCacheObject,
  OperationVariables,
  QueryOptions,
  createHttpLink,
} from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { ErrorCodes, StandardError } from '@getvim-os/errors';
import { OperationDefinitionNode } from 'graphql';
export { gql } from '@apollo/client';
import { otelInstrumentErrorLink, otelInstrumentLink } from '@getvim/opentelemetry-sdk/apollo/link';

const RETRIES_ERRORS_WHITELIST = [
  'Failed to fetch',
  'ECONNRESET',
  'ENOTFOUND',
  'EHOSTUNREACH',
  'ECONNREFUSED',
  'socket hang up',
];

function isNetworkError(error) {
  return (
    error &&
    (RETRIES_ERRORS_WHITELIST.includes(error.message) ||
      RETRIES_ERRORS_WHITELIST.find(
        (errorCode) => error.code === errorCode || error.message.includes(errorCode),
      ))
  );
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 250;

export class NoAccessTokenError extends StandardError {
  constructor(context) {
    super('No Access Token Received', ErrorCodes.UNAUTHORIZED, context);
  }
}

export class VimGraphQLClient extends ApolloClient<NormalizedCacheObject> {
  private accessToken?: string;
  private deviceId?: string;

  constructor(uri: string = '/api/graphql') {
    const httpLink = createHttpLink({ uri });

    const authLink = setContext((_, { headers }) => {
      // return the headers to the context so httpLink can read them
      return {
        headers: {
          ...(this.accessToken ? { authorization: this.accessToken } : undefined),
          ...(this.deviceId ? { ['x-vim-device-id']: this.deviceId } : undefined),
          ...headers,
        },
      };
    });

    const errorLink = new ApolloLink((operation, forward) => {
      return forward(operation).map((data) => {
        if (data && data.errors) {
          const networkError = data.errors.find((element) => isNetworkError(element));
          if (networkError) {
            throw new Error(networkError.message);
          }
        }
        return data;
      });
    });

    const retryLink = new RetryLink({
      delay: {
        initial: RETRY_DELAY,
        max: 2500,
        jitter: true,
      },
      attempts: {
        max: MAX_RETRIES,
        retryIf: (error, operation) => {
          // on 504s cloudflare returns an html page including "vim is baking ..."
          const isBaking = error?.bodyText?.toLowerCase().includes('baking');

          const operationType = (operation.query.definitions[0] as OperationDefinitionNode)
            ?.operation;
          const isQueryOperation = operationType === 'query';

          return isQueryOperation && (isBaking || isNetworkError(error));
        },
      },
    });

    super({
      link: ApolloLink.from([
        otelInstrumentErrorLink,
        otelInstrumentLink,
        retryLink,
        authLink,
        errorLink,
        httpLink,
      ]),
      cache: new InMemoryCache({ resultCaching: true }),
    });
  }

  public setAccessToken(token: string) {
    this.accessToken = token;
  }

  public setDeviceId(deviceId: string) {
    this.deviceId = deviceId;
  }

  /* istanbul ignore next 3  -- @preserve */
  public getDeviceId() {
    return this.deviceId;
  }

  async mutateAuthenticated<
    TData = unknown,
    TVariables extends OperationVariables = OperationVariables,
    TContext extends Record<string, unknown> = DefaultContext,
  >(options: MutationOptions<TData, TVariables, TContext>): Promise<FetchResult<TData>> {
    if (!this.accessToken) {
      throw new NoAccessTokenError({
        options,
        accessToken: this.accessToken,
        deviceId: this.deviceId,
      });
    }

    return await this.mutate(options);
  }

  async mutate<
    TData = unknown,
    TVariables extends OperationVariables = OperationVariables,
    TContext extends Record<string, unknown> = DefaultContext,
  >(options: MutationOptions<TData, TVariables, TContext>): Promise<FetchResult<TData>> {
    try {
      return await super.mutate(options);
    } catch (error) {
      if (error) {
        (error as any).options = options;
      }
      throw error;
    }
  }

  async query<T = unknown, TVariables extends OperationVariables = OperationVariables>(
    options: QueryOptions<TVariables, T>,
  ): Promise<ApolloQueryResult<T>> {
    try {
      return await super.query(options);
    } catch (error) {
      if (error) {
        (error as any).options = options;
      }
      throw error;
    }
  }

  public async queryAuthenticated<
    T = unknown,
    TVariables extends OperationVariables = OperationVariables,
  >(options: QueryOptions<TVariables>): Promise<ApolloQueryResult<T>> {
    if (!this.accessToken) {
      throw new NoAccessTokenError({
        options,
        accessToken: this.accessToken,
        deviceId: this.deviceId,
      });
    }

    return await this.query<T, OperationVariables>(options);
  }
}
